import React, { useState, useRef, useCallback } from 'react';
import './DragDropUploader.css';

const IMAGE_REGEX = /\.(jpg|jpeg|webp|tiff)$/i;

type EntryCapableDataTransferItem = DataTransferItem & {
    webkitGetAsEntry?: () => FileSystemEntry | null;
};

type FileSystemFileEntry = FileSystemEntry & {
    file: (
        successCallback: (file: File) => void,
        errorCallback?: (error: DOMException) => void
    ) => void;
};

const isDirectoryEntry = (entry: FileSystemEntry | null): entry is FileSystemDirectoryEntry =>
    Boolean(entry && entry.isDirectory);

const isFileEntry = (entry: FileSystemEntry | null): entry is FileSystemFileEntry =>
    Boolean(entry && entry.isFile);

const normalizeEntryPath = (path: string) => path.replace(/^\/+/, '');

const buildFileKey = (rootFolderName: string, currentPath: string, filename: string) => {
    const parts = [];
    if (rootFolderName) parts.push(rootFolderName);
    if (currentPath) parts.push(currentPath);
    parts.push(filename);
    return parts.join('/');
};

const readAllEntries = async (reader: FileSystemDirectoryReader) => {
    const entries: FileSystemEntry[] = [];
    while (true) {
        const batch = await new Promise<FileSystemEntry[]>((resolve, reject) => {
            reader.readEntries(
                (results) => resolve(results),
                (error) => reject(error)
            );
        });
        if (!batch.length) break;
        entries.push(...batch);
    }
    return entries;
};

interface FileWithFolder extends File {
    _folderPath?: string;
}

interface DragDropUploaderProps {
    onFilesUploaded: (files: File[], folderName: string) => void;
    onTriggerFolderPicker: () => void;
    onCheckCredits?: (fileCount: number) => boolean;
    onCheckUploadsAllowed?: () => boolean;
    clearToken?: number;
    disabled?: boolean;
    isAuthenticated: boolean;
    onRequireAuth: () => void;
}

const DragDropUploader: React.FC<DragDropUploaderProps> = ({
    onFilesUploaded,
    onTriggerFolderPicker,
    onCheckCredits,
    onCheckUploadsAllowed,
    clearToken,
    disabled = false,
    isAuthenticated,
    onRequireAuth
}) => {
    const [imageFiles, setImageFiles] = useState<FileWithFolder[]>([]);
    const [folderPaths, setFolderPaths] = useState<string[]>([]);
    const [totalSizeMB, setTotalSizeMB] = useState(0);

    React.useEffect(() => {
        setImageFiles([]);
        setFolderPaths([]);
        setTotalSizeMB(0);
    }, [clearToken]);

    const ensureAuthenticated = useCallback(() => {
        if (!isAuthenticated) {
            onRequireAuth();
            return false;
        }
        return true;
    }, [isAuthenticated, onRequireAuth]);

    const ensureUploadsAllowed = useCallback(() => {
        if (!onCheckUploadsAllowed) {
            return true;
        }
        return onCheckUploadsAllowed();
    }, [onCheckUploadsAllowed]);

    const finalizeUpload = useCallback(
        (files: FileWithFolder[], folders: string[], rootFolderName: string) => {
            if (onCheckCredits && !onCheckCredits(files.length)) {
                return false;
            }
            setImageFiles(files);
            const totalSize = files.reduce((acc, file) => acc + file.size, 0);
            setTotalSizeMB(Number((totalSize / 1024 / 1024).toFixed(2)));
            setFolderPaths(folders);
            onFilesUploaded(files, rootFolderName);
            return true;
        },
        [onCheckCredits, onFilesUploaded]
    );

    // Funzione asincrona per estrarre file immagine e percorsi dalle cartelle
    // Funzione ricorsiva per attraversare directory
    const traverseDirectory = useCallback(async (
        directory: FileSystemDirectoryEntry,
        currentPath: string,
        rootFolderName: string,
        folderPathsArray: string[],
        processedPaths: Set<string>,
        seenFiles: Set<string>,
        imageFilesArray: FileWithFolder[]
    ) => {
        if (currentPath && !processedPaths.has(currentPath)) {
            folderPathsArray.push(currentPath);
            processedPaths.add(currentPath);
        }

        const reader = directory.createReader();

        try {
            const entries = await readAllEntries(reader);
            console.log(`Processing directory '${directory.name}', found ${entries.length} entries`);
            for (const entry of entries) {
                if (isDirectoryEntry(entry)) {
                    const newPath = currentPath ? `${currentPath}/${entry.name}` : entry.name;
                    await traverseDirectory(
                        entry,
                        newPath,
                        rootFolderName,
                        folderPathsArray,
                        processedPaths,
                        seenFiles,
                        imageFilesArray
                    );
                } else if (isFileEntry(entry)) {
                    if (entry.name && IMAGE_REGEX.test(entry.name)) {
                        const rawPath = normalizeEntryPath(entry.fullPath || '');
                        const fileKey = rawPath || buildFileKey(rootFolderName, currentPath, entry.name);
                        if (seenFiles.has(fileKey)) {
                            continue;
                        }
                        seenFiles.add(fileKey);
                        try {
                            const file = await new Promise<File>((resolve, reject) => {
                                entry.file(
                                    (f: File) => resolve(f),
                                    (error: DOMException) => reject(error)
                                );
                            });
                            const fullPath = rootFolderName + (currentPath ? '/' + currentPath : '');
                            const enrichedFile: FileWithFolder = file;
                            enrichedFile._folderPath = fullPath;
                            imageFilesArray.push(enrichedFile);
                        } catch (err) {
                            console.error("Error reading file:", entry.name, err);
                        }
                    }
                }
            }
        } catch (err) {
            console.error("Error reading directory entries:", err);
            return;
        }
    }, []);

    const processDirectory = useCallback(async (items: DataTransferItemList) => {
        const imageFilesArray: FileWithFolder[] = [];
        const folderPathsArray: string[] = [];
        const processedPaths = new Set<string>();
        const seenFiles = new Set<string>();
        let rootFolderName = "";

        const firstItem = items[0] as EntryCapableDataTransferItem | undefined;
        const firstEntry = firstItem?.webkitGetAsEntry?.() ?? null;
        if (firstEntry && firstEntry.isDirectory) {
            rootFolderName = firstEntry.name;
        }

        for (const item of Array.from(items)) {
            const entryCapable = item as EntryCapableDataTransferItem;
            const entry = entryCapable.webkitGetAsEntry?.() ?? null;

            if (isDirectoryEntry(entry)) {
                await traverseDirectory(
                    entry,
                    '',
                    entry.name,
                    folderPathsArray,
                    processedPaths,
                    seenFiles,
                    imageFilesArray
                );
            } else if (isFileEntry(entry)) {
                if (entry.name && IMAGE_REGEX.test(entry.name)) {
                    const rawPath = normalizeEntryPath(entry.fullPath || '');
                    const fileKey = rawPath || buildFileKey(rootFolderName, '', entry.name);
                    if (seenFiles.has(fileKey)) {
                        continue;
                    }
                    seenFiles.add(fileKey);
                    try {
                        const file = await new Promise<File>((resolve, reject) => {
                            entry.file(
                                (f: File) => resolve(f),
                                (error: DOMException) => reject(error)
                            );
                        });
                        const enrichedFile: FileWithFolder = file;
                        enrichedFile._folderPath = '';
                        imageFilesArray.push(enrichedFile);
                    } catch (err) {
                        console.error("Error reading file:", entry.name, err);
                    }
                }
            } else {
                const file = item.getAsFile();
                if (file && (file.type.startsWith('image/') || IMAGE_REGEX.test(file.name))) {
                    const enrichedFile: FileWithFolder = file;
                    enrichedFile._folderPath = '';
                    imageFilesArray.push(enrichedFile);
                }
            }
        }

        finalizeUpload(imageFilesArray, Array.from(processedPaths), rootFolderName);
    }, [finalizeUpload, traverseDirectory]);

    const processFileList = useCallback((fileList: FileList) => {
        const imageFilesArray: FileWithFolder[] = [];
        const processedPaths = new Set<string>();
        const seenFiles = new Set<string>();

        Array.from(fileList).forEach((file) => {
            if (file && (file.type.startsWith('image/') || IMAGE_REGEX.test(file.name))) {
                const enrichedFile: FileWithFolder = file;
                const relativePath = (file as File & { webkitRelativePath?: string }).webkitRelativePath;
                const fileKey = relativePath || `${file.name}:${file.size}:${file.lastModified}`;
                if (seenFiles.has(fileKey)) {
                    return;
                }
                seenFiles.add(fileKey);
                if (relativePath) {
                    const folderPath = relativePath.split('/').slice(0, -1).join('/');
                    enrichedFile._folderPath = folderPath || '';
                    if (folderPath) {
                        processedPaths.add(folderPath);
                    }
                } else {
                    enrichedFile._folderPath = '';
                }
                imageFilesArray.push(enrichedFile);
            }
        });

        finalizeUpload(imageFilesArray, Array.from(processedPaths), "");
    }, [finalizeUpload]);

    const handleDrop = useCallback(async (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        if (disabled) return;
        if (!ensureAuthenticated()) return;
        if (!ensureUploadsAllowed()) return;

        const { items, files } = e.dataTransfer;
        if (items && items.length > 0) {
            const itemsArray = Array.from(items) as EntryCapableDataTransferItem[];
            const hasDirectory = itemsArray.some((item) => {
                const entry = item.webkitGetAsEntry?.() ?? null;
                return isDirectoryEntry(entry);
            });

            if (hasDirectory) {
                await processDirectory(items);
                return;
            }

            if (files && files.length > 0) {
                processFileList(files);
                return;
            }

            await processDirectory(items);
            return;
        }

        if (files && files.length > 0) {
            processFileList(files);
            return;
        }

        alert('No drag & drop support for directories in this browser.');
    }, [disabled, ensureAuthenticated, ensureUploadsAllowed, processDirectory, processFileList]);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!ensureAuthenticated()) {
            e.target.value = '';
            return;
        }
        if (!ensureUploadsAllowed()) {
            e.target.value = '';
            return;
        }
        if (files && files.length > 0) {
            const fileArray = Array.from(files);
            const imageFiles = fileArray.filter(file =>
                file.type.startsWith('image/') ||
                file.name.match(/\.(jpg|jpeg|webp|tiff)$/i)
            );

            if (imageFiles.length > 0) {
                const didUpload = finalizeUpload(imageFiles, [], "");
                if (didUpload) {
                    // Trigger the existing folderPicker after files are uploaded
                    onTriggerFolderPicker();
                }
            } else {
                alert('No image file found. Only drop image files (jpg, jpeg, webp, tiff).');
            }
        }
        e.target.value = '';
    }, [ensureAuthenticated, ensureUploadsAllowed, finalizeUpload, onTriggerFolderPicker]);

    const openFileDialog = useCallback(() => {
        if (!disabled && fileInputRef.current) {
            if (!ensureAuthenticated()) {
                return;
            }
            if (!ensureUploadsAllowed()) {
                return;
            }
            fileInputRef.current.click();
        }
    }, [disabled, ensureAuthenticated, ensureUploadsAllowed]);

    return (
        <div className="drag-drop-container">
            <div
                className={`drag-drop-zone ${disabled ? 'is-disabled' : ''}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
                onClick={openFileDialog}
            >
                <div className="drag-drop-zone__icon">📁</div>
                <h3>
                    {disabled ? 'Uploading in progress...' : 'Drop folders or files here'}
                </h3>
                <p>
                    {disabled
                        ? 'Wait for the current upload to finish.'
                        : "To keep GPS location, select 'Browse' > 'Images Folder' instead of using photos with virtual paths (recent, cloud, etc)."
                    }
                </p>
                <div className="drag-drop-zone__meta">Supported formats: JPG, JPEG, WebP, TIFF</div>
            </div>

            {/* Hidden file input for individual file selection */}
            <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
            />

            {/* Display uploaded files */}
            {imageFiles.length > 0 && (
                <div className="uploaded-files">
                    <div className="uploaded-files__header">
                        <h4>Files loaded</h4>
                        <div className="uploaded-files__badges">
                            <span className="uploaded-files__badge"><span className="notranslate">{imageFiles.length}</span> files</span>
                            <span className="uploaded-files__badge uploaded-files__badge--soft"><span className="notranslate">{totalSizeMB}</span> MB</span>
                        </div>
                    </div>
                    {folderPaths.length > 0 && (
                        <div className="uploaded-files__folders">
                            <strong>Folder paths found:</strong>
                            <ul>
                                {folderPaths.map((path) => (
                                    <li key={path}>{path}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    <div className="uploaded-files__list">
                        {imageFiles.map((file, index) => (
                            <details key={`${file.name}-${index}`} className="uploaded-files__item">
                                <summary>📄 {file.name}{file._folderPath ? ` (in: ${file._folderPath})` : ''}</summary>
                                <small className="uploaded-files__size">
                                    <span className="notranslate">{(file.size / 1048576).toFixed(2)}</span> MB
                                </small>
                            </details>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DragDropUploader;
